import { useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../../context/AuthContext";
import { getSchoolYearLabel, useSchoolYear } from "../../context/SchoolYearContext";
import { useClassSettings, useExpenseDocumentImports } from "../../hooks/useData";
import ClassLogo from "../ui/ClassLogo";

const navItems = [
  { to: "/dashboard", icon: "⬡", label: "Dashboard" },
  { to: "/collection", icon: "◈", label: "Zbiórka" },
  { to: "/expenses", icon: "≡", label: "Rozliczenie" },
  { to: "/planned", icon: "◷", label: "Planowane" },
  { to: "/notices", icon: "◻", label: "Przypomnienia" },
];

function statusLabel(status) {
  switch (status) {
    case "active":
      return "aktywny";
    case "draft":
      return "wersja robocza";
    case "archived":
      return "archiwum";
    default:
      return "";
  }
}

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { signOut, isAdmin } = useAuth();
  const { data: settings } = useClassSettings();
  const { selectedSchoolYear, loading: schoolYearLoading } = useSchoolYear();
  const { count: pendingDocumentCount } = useExpenseDocumentImports({
    countOnly: true,
    enabled: isAdmin,
  });
  const navigate = useNavigate();

  async function handleSignOut() {
    await signOut();
    navigate("/login");
  }

  const allNav = isAdmin
    ? [
        ...navItems,
        {
          to: "/admin/documents",
          icon: "▤",
          label: "Dokumenty",
          badge: pendingDocumentCount,
        },
        { to: "/admin", icon: "◆", label: "Admin" },
        { to: "/settings", icon: "◎", label: "Ustawienia" },
      ]
    : [...navItems, { to: "/settings", icon: "◎", label: "Ustawienia" }];

  const selectedSchoolYearLabel = getSchoolYearLabel(selectedSchoolYear);
  const selectedSchoolYearStatus = statusLabel(selectedSchoolYear?.status);
  const schoolYearText = schoolYearLoading
    ? "Ładowanie roku…"
    : selectedSchoolYearLabel || settings?.school_year || "Rok szkolny";
  const schoolYearDisplay =
    !schoolYearLoading && selectedSchoolYearStatus
      ? `${schoolYearText} · ${selectedSchoolYearStatus}`
      : schoolYearText;

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#f6fbfa] text-slate-800">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(167,243,208,0.24),_transparent_26%),radial-gradient(circle_at_top_right,_rgba(253,224,171,0.22),_transparent_24%),radial-gradient(circle_at_bottom_left,_rgba(186,230,253,0.18),_transparent_28%),linear-gradient(180deg,#f8fcfb_0%,#eef8f6_100%)]" />

        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full border border-emerald-200/70" />
        <div className="absolute -top-8 -left-8 h-44 w-44 rounded-full bg-emerald-200/35 blur-2xl" />

        <div className="absolute top-[14%] right-[10%] h-20 w-20 rounded-full bg-amber-200/35 blur-xl" />
        <div className="absolute bottom-[14%] right-[4%] h-72 w-72 rounded-full bg-amber-200/25 blur-3xl" />
        <div className="absolute bottom-[10%] left-[30%] h-52 w-52 rounded-full bg-teal-200/20 blur-3xl" />

        <div className="absolute left-[8%] top-[38%] h-[1px] w-[38%] rounded-full bg-emerald-200/70" />
        <div className="absolute left-[10%] top-[41%] h-[1px] w-[34%] rounded-full bg-sky-200/60" />
        <div className="absolute left-[12%] top-[44%] h-[1px] w-[30%] rounded-full bg-amber-100/80" />

        <div className="absolute left-[4%] top-[46%] h-2 w-2 rounded-full bg-teal-300/80 shadow-[0_0_18px_rgba(45,212,191,0.35)]" />
        <div className="absolute left-[40%] top-[30%] h-4 w-4 rounded-full bg-amber-300/75 shadow-[0_0_20px_rgba(251,191,36,0.25)]" />
        <div className="absolute right-[18%] top-[58%] h-3 w-3 rounded-full bg-orange-300/70 shadow-[0_0_16px_rgba(251,146,60,0.25)]" />
      </div>

      <div className="relative flex min-h-screen">
        <aside className="hidden lg:flex fixed left-0 top-0 z-30 h-full w-72 flex-col border-r border-emerald-100/80 bg-white/72 backdrop-blur-xl shadow-[0_10px_40px_rgba(15,23,42,0.06)]">
          <div className="px-6 pt-7 pb-5 border-b border-emerald-100/80">
            <div className="flex items-center gap-4">
              <div className="shrink-0 rounded-2xl bg-white/80 p-1.5 shadow-[0_8px_24px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100/80">
                <ClassLogo size="md" />
              </div>

              <div className="min-w-0">
                <div className="font-display text-base font-semibold tracking-tight text-slate-900">
                  {settings?.class_name ?? "Klasa"}
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  {schoolYearDisplay}
                </div>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-emerald-100/80 bg-gradient-to-r from-emerald-50 via-teal-50 to-amber-50 px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700/80">
                Panel klasy
              </div>
              <div className="mt-1 text-sm leading-5 text-slate-600">
                Przejrzyste rozliczenia, zbiórki i wydatki w jednym miejscu.
              </div>
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-5">
            <div className="mb-3 px-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Menu
            </div>

            <div className="space-y-2">
              {allNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-white text-slate-900 shadow-[0_10px_24px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100"
                        : "text-slate-500 hover:bg-white/75 hover:text-slate-800 hover:shadow-[0_8px_18px_rgba(15,23,42,0.05)]"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={`flex h-9 w-9 items-center justify-center rounded-xl text-base transition-all duration-200 ${
                          isActive
                            ? "bg-gradient-to-br from-emerald-100 to-amber-100 text-emerald-700"
                            : "bg-slate-100/80 text-slate-500 group-hover:bg-emerald-50 group-hover:text-emerald-700"
                        }`}
                      >
                        {item.icon}
                      </span>

                      <span className="flex-1">{item.label}</span>

                      {item.badge > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                          {item.badge}
                        </span>
                      )}

                      <span
                        className={`h-2 w-2 rounded-full transition-all duration-200 ${
                          isActive
                            ? "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.55)]"
                            : "bg-transparent"
                        }`}
                      />
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </nav>

          <div className="border-t border-emerald-100/80 p-4">
            <button
              onClick={handleSignOut}
              className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-medium text-slate-500 transition-all duration-200 hover:bg-white hover:text-red-500 hover:shadow-[0_8px_18px_rgba(15,23,42,0.05)]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100/80 text-base">
                ⊘
              </span>
              Wyloguj
            </button>
          </div>
        </aside>

        <div className="lg:hidden fixed left-0 right-0 top-0 z-40 border-b border-white/60 bg-white/78 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.06)]">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="rounded-2xl bg-white/80 p-1.5 shadow-[0_6px_18px_rgba(15,23,42,0.07)] ring-1 ring-emerald-100/80">
                <ClassLogo size="sm" />
              </div>

              <div className="min-w-0">
                <div className="truncate font-display text-sm font-semibold text-slate-900">
                  {settings?.class_name ?? "Klasa"}
                </div>
                <div className="truncate text-xs text-slate-500">
                  {schoolYearDisplay}
                </div>
              </div>
            </div>

            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100/80 text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="lg:hidden fixed inset-0 z-40 bg-slate-900/35 backdrop-blur-sm"
                onClick={() => setMobileOpen(false)}
              />

              <motion.aside
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 280 }}
                className="lg:hidden fixed left-0 top-0 z-50 flex h-full w-80 max-w-[88vw] flex-col border-r border-emerald-100/80 bg-white/95 backdrop-blur-2xl shadow-[0_20px_60px_rgba(15,23,42,0.14)]"
              >
                <div className="border-b border-emerald-100/80 px-5 pt-5 pb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="rounded-2xl bg-white p-1.5 shadow-[0_6px_18px_rgba(15,23,42,0.07)] ring-1 ring-emerald-100/80">
                        <ClassLogo size="md" />
                      </div>

                      <div className="min-w-0">
                        <div className="truncate font-display text-sm font-semibold text-slate-900">
                          {settings?.class_name ?? "Klasa"}
                        </div>
                        <div className="truncate text-xs text-slate-500">
                          {schoolYearDisplay}
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setMobileOpen(false)}
                      className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100/80 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="mt-4 rounded-2xl border border-emerald-100/80 bg-gradient-to-r from-emerald-50 via-teal-50 to-amber-50 px-4 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700/80">
                      Panel klasy
                    </div>
                    <div className="mt-1 text-sm leading-5 text-slate-600">
                      Wszystkie najważniejsze informacje w jednym miejscu.
                    </div>
                  </div>
                </div>

                <nav className="flex-1 overflow-y-auto px-4 py-5">
                  <div className="space-y-2">
                    {allNav.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={({ isActive }) =>
                          `group flex items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? "bg-white text-slate-900 shadow-[0_10px_24px_rgba(15,23,42,0.08)] ring-1 ring-emerald-100"
                              : "text-slate-500 hover:bg-white hover:text-slate-800"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              className={`flex h-9 w-9 items-center justify-center rounded-xl text-base transition-all duration-200 ${
                                isActive
                                  ? "bg-gradient-to-br from-emerald-100 to-amber-100 text-emerald-700"
                                  : "bg-slate-100/80 text-slate-500 group-hover:bg-emerald-50 group-hover:text-emerald-700"
                              }`}
                            >
                              {item.icon}
                            </span>

                            <span className="flex-1">{item.label}</span>

                            {item.badge > 0 && (
                              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
                                {item.badge}
                              </span>
                            )}

                            <span
                              className={`h-2 w-2 rounded-full transition-all duration-200 ${
                                isActive
                                  ? "bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.55)]"
                                  : "bg-transparent"
                              }`}
                            />
                          </>
                        )}
                      </NavLink>
                    ))}
                  </div>
                </nav>

                <div className="border-t border-emerald-100/80 p-4 space-y-3">
                  <Link
                    to="/privacy"
                    onClick={() => setMobileOpen(false)}
                    className="flex w-full items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 transition-all duration-200 hover:border-emerald-200 hover:text-emerald-700"
                  >
                    Polityka prywatności
                  </Link>

                  <button
                    onClick={handleSignOut}
                    className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-sm font-medium text-slate-500 transition-all duration-200 hover:bg-slate-50 hover:text-red-500"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100/80 text-base">
                      ⊘
                    </span>
                    Wyloguj
                  </button>
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>

        <main className="relative flex-1 pt-[76px] lg:ml-72 lg:pt-0">
          <div className="min-h-screen flex flex-col">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="mx-auto w-full max-w-7xl flex-1 px-4 py-6 sm:px-6 lg:px-10 lg:py-10"
            >
              {children}
            </motion.div>

            <footer className="mx-auto w-full max-w-7xl px-4 pb-8 pt-2 text-center sm:px-6 lg:px-10">
              <div className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-white/60 bg-white/60 px-6 py-4 text-sm text-slate-500 shadow-[0_10px_30px_rgba(15,23,42,0.04)] backdrop-blur-sm sm:flex-row sm:justify-between">
                <Link
                  to="/privacy"
                  className="font-medium text-slate-600 transition-colors hover:text-emerald-700"
                >
                  Polityka prywatności
                </Link>

                <div className="text-xs text-slate-400">
                  Projekt i realizacja:{" "}
                  <a
                    href="https://rbaio.eu"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-slate-500 transition-colors hover:text-emerald-700"
                  >
                    rbaio.eu
                  </a>
                </div>
              </div>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
